/**
 * @fileoverview Service Bus Explorer — search queues & topic subscriptions.
 * @description Usability-first redesign: pick (or create) a saved connection,
 *              then build criteria and search. Connection setup lives in a
 *              dedicated dialog to keep the main view uncluttered.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Stack, Alert,
  Chip, CircularProgress, Fade, Grow, Divider, Tooltip, IconButton,
  InputAdornment, Collapse, ToggleButton, ToggleButtonGroup, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Search, Database, Clock, AlertTriangle, Hash, Copy, CheckCircle, Inbox,
  Plus, Trash2, ChevronDown, ChevronUp, Link2, Layers, ArrowDownToLine,
  Sparkles, RefreshCw, Pencil, Download, Maximize2, X,
} from 'lucide-react';
import { serviceBusExplorerApi } from '../services/serviceBusExplorerApi';
import ConnectionDialog from '../components/ConnectionDialog';

/* ── Shared sx tokens ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0097A9' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0097A9', borderWidth: 2,
  },
};

/* Derive the short field name (leaf) from a JSON dot-path. */
function leafOf(path) {
  if (!path) return '';
  const seg = String(path).split('.').pop();
  return seg.replace(/\[\d+\]$/, '');
}

/* ── Copy button ── */
function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setOk(true);
    setTimeout(() => setOk(false), 1500);
  };
  return (
    <Tooltip title={ok ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={copy} sx={{ '&:hover': { bgcolor: 'rgba(0,151,169,0.08)' } }}>
        {ok ? <CheckCircle size={14} color="#00AF66" /> : <Copy size={14} color="#64748b" />}
      </IconButton>
    </Tooltip>
  );
}

/* ── DL Reason Chip ── */
function DLReasonChip({ reason }) {
  const config = {
    MaxDeliveryCountExceeded: { color: '#dc2626', bg: '#fef2f2', label: 'Max Delivery Exceeded' },
    HeaderSizeExceeded: { color: '#d97706', bg: '#fffbeb', label: 'Header Too Large' },
    TTLExpiredException: { color: '#7c3aed', bg: '#f5f3ff', label: 'TTL Expired' },
  };
  const c = config[reason] || { color: '#64748b', bg: '#f8fafc', label: reason || 'Unknown' };
  return (
    <Chip
      label={c.label} size="small" icon={<AlertTriangle size={12} />}
      sx={{
        bgcolor: c.bg, color: c.color, fontWeight: 600, fontSize: '0.7rem',
        border: `1px solid ${c.color}22`,
        '& .MuiChip-icon': { color: c.color },
      }}
    />
  );
}

/* Small kind chip (Queue / Topic). */
function KindChip({ kind, height = 20 }) {
  const isTopic = kind === 'topic';
  return (
    <Chip
      size="small"
      label={isTopic ? 'Topic' : 'Queue'}
      sx={{
        height, fontWeight: 700, fontSize: '0.62rem',
        bgcolor: isTopic ? '#eef2ff' : '#ecfeff',
        color: isTopic ? '#4f46e5' : '#0891b2',
      }}
    />
  );
}

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const ServiceBusExplorer = () => {
  /* Connections */
  const [activeConnection, setActiveConnection] = useState(null);
  const [savedConnections, setSavedConnections] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);

  /* Search */
  const [queueTarget, setQueueTarget] = useState('deadletter');
  const [criteria, setCriteria] = useState([{ key: '', value: '' }]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const [fieldOptions, setFieldOptions] = useState([]);
  const [sampling, setSampling] = useState(false);
  const [sampleInfo, setSampleInfo] = useState('');
  const [viewer, setViewer] = useState(null);

  /* ── Criteria management ── */
  const addCriterion = () => setCriteria(prev => [...prev, { key: '', value: '' }]);
  const removeCriterion = (idx) => setCriteria(prev => prev.filter((_, i) => i !== idx));
  const updateCriterion = (idx, field, val) =>
    setCriteria(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  /* ── Saved connections (per-user) ── */
  const loadSavedConnections = useCallback(async () => {
    setSavedLoading(true);
    try {
      const res = await serviceBusExplorerApi.listConnections();
      setSavedConnections(res.connections || []);
    } catch {
      /* non-blocking — saved connections are optional */
    } finally {
      setSavedLoading(false);
    }
  }, []);

  useEffect(() => { loadSavedConnections(); }, [loadSavedConnections]);

  const activateConnection = useCallback((conn) => {
    setActiveConnection(conn);
    setResults(null);
    setFieldOptions([]);
    setSampleInfo('');
    setError('');
  }, []);

  const handlePickSaved = useCallback(async (conn) => {
    try {
      const res = await serviceBusExplorerApi.revealConnection(conn.id);
      if (!res.connection_string) { setError('Could not load that connection.'); return; }
      activateConnection({
        id: conn.id,
        name: conn.name,
        kind: res.kind || conn.kind || 'queue',
        entityName: res.entity_name || conn.entity_name || '',
        subscriptionName: res.subscription_name || conn.subscription_name || '',
        connectionString: res.connection_string,
      });
    } catch (err) {
      setError(err.message || 'Failed to load saved connection.');
    }
  }, [activateConnection]);

  const handleDeleteSaved = useCallback(async (id) => {
    try {
      await serviceBusExplorerApi.deleteConnection(id);
      if (activeConnection?.id === id) setActiveConnection(null);
      await loadSavedConnections();
    } catch (err) {
      setError(err.message || 'Failed to delete connection.');
    }
  }, [activeConnection, loadSavedConnections]);

  const openNewDialog = () => { setDialogInitial(null); setDialogOpen(true); };
  const openEditDialog = () => { if (activeConnection) { setDialogInitial(activeConnection); setDialogOpen(true); } };
  const handleDialogSaved = useCallback(async (conn) => {
    setDialogOpen(false);
    await loadSavedConnections();
    activateConnection(conn);
  }, [loadSavedConnections, activateConnection]);

  /* Effective search params from the active connection. */
  const searchSubscription = activeConnection?.kind === 'topic' ? (activeConnection.subscriptionName || '') : '';

  /* ── Load field paths from a sample message ── */
  const handleLoadFields = useCallback(async () => {
    if (!activeConnection) { setError('Select a connection first.'); return; }
    setError('');
    setSampling(true);
    try {
      const res = await serviceBusExplorerApi.sampleFields(
        activeConnection.connectionString,
        activeConnection.entityName || '',
        queueTarget,
        activeConnection.kind === 'topic' ? (activeConnection.subscriptionName || '') : '',
      );
      if (res.error) {
        setError(res.error);
        setFieldOptions([]);
        setSampleInfo('');
      } else {
        const fields = res.fields || [];
        setFieldOptions(fields);
        setSampleInfo(
          fields.length
            ? `${fields.length} field${fields.length > 1 ? 's' : ''} loaded from a sample message`
            : 'No JSON fields found in the sampled message.',
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to load fields.');
    } finally {
      setSampling(false);
    }
  }, [activeConnection, queueTarget]);

  /* ── Search handler ── */
  const handleSearch = useCallback(async () => {
    if (!activeConnection) { setError('Select a connection first.'); return; }
    const validCriteria = criteria.filter(c => c.key.trim() && c.value.trim());
    if (validCriteria.length === 0) {
      setError('Add at least one search field and value.');
      return;
    }

    setError('');
    setResults(null);
    setLoading(true);
    try {
      const res = await serviceBusExplorerApi.search(
        activeConnection.connectionString,
        validCriteria,
        activeConnection.entityName || '',
        queueTarget,
        activeConnection.kind === 'topic' ? (activeConnection.subscriptionName || '') : '',
      );
      if (res.error) setError(res.error);
      setResults(res);
    } catch (err) {
      setError(err.message || 'Failed to search.');
    } finally {
      setLoading(false);
    }
  }, [activeConnection, queueTarget, criteria]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) handleSearch();
  };

  const toggleCard = (seq) =>
    setExpandedCards(prev => ({ ...prev, [seq]: !prev[seq] }));

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Fade in timeout={400}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5, mb: 3, borderRadius: '16px',
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Database size={26} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Service Bus Explorer
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Search queues & topic subscriptions with deep JSON path matching.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Fade>

      {/* Connection */}
      <Fade in timeout={600}>
        <Paper elevation={0} sx={{ p: activeConnection ? 2 : 3, mb: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          {!activeConnection ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2" fontWeight={700} color="#01426A">
                <Link2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Choose a connection
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pick one of your saved connections or create a new one. Your connections are private to you.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Autocomplete
                  fullWidth size="small"
                  options={savedConnections}
                  loading={savedLoading}
                  getOptionLabel={(o) => o.name || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={null}
                  blurOnSelect
                  onChange={(_, val) => { if (val) handlePickSaved(val); }}
                  noOptionsText="No saved connections yet"
                  renderOption={(props, option) => (
                    <Box
                      component="li" {...props}
                      sx={{ display: 'flex !important', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{option.name}</Typography>
                          {option.kind && <KindChip kind={option.kind} height={18} />}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {option.preview}
                        </Typography>
                      </Box>
                      <Tooltip title="Delete saved connection">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSaved(option.id); }}
                          sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Saved connections"
                      placeholder={savedConnections.length ? 'Select a saved connection' : 'No saved connections yet'}
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start"><Database size={16} color="#64748b" /></InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained" startIcon={<Plus size={16} />} onClick={openNewDialog}
                  sx={{
                    bgcolor: '#0097A9', borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                    whiteSpace: 'nowrap', px: 2.5, '&:hover': { bgcolor: '#007f8f' },
                  }}
                >
                  New connection
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px', bgcolor: '#0f172a',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Database size={20} color="#fff" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight={700} color="#01426A" noWrap>
                      {activeConnection.name}
                    </Typography>
                    <KindChip kind={activeConnection.kind} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.25} flexWrap="wrap">
                    {activeConnection.entityName && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {activeConnection.kind === 'topic' ? 'topic' : 'queue'}: {activeConnection.entityName}
                      </Typography>
                    )}
                    {activeConnection.kind === 'topic' && activeConnection.subscriptionName && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        • sub: {activeConnection.subscriptionName}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Button
                  size="small" variant="text" startIcon={<Pencil size={14} />} onClick={openEditDialog}
                  sx={{ color: '#0097A9', textTransform: 'none', fontWeight: 600 }}
                >
                  Edit
                </Button>
                <Button
                  size="small" variant="outlined" startIcon={<RefreshCw size={14} />}
                  onClick={() => setActiveConnection(null)}
                  sx={{ borderColor: '#cbd5e1', color: '#475569', textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                >
                  Change
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Fade>

      {activeConnection && (
        <>
          {/* Search */}
          <Fade in timeout={800}>
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
              {/* Target + actions */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5} mb={2}
              >
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.75}>
                    Search where
                  </Typography>
                  <ToggleButtonGroup
                    value={queueTarget} exclusive size="small"
                    onChange={(_, val) => { if (val) setQueueTarget(val); }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                        borderRadius: '8px', px: 2, py: 0.75,
                        '&.Mui-selected': { bgcolor: '#0097A9', color: '#fff', '&:hover': { bgcolor: '#007f8f' } },
                      },
                    }}
                  >
                    <ToggleButton value="deadletter"><Inbox size={14} style={{ marginRight: 6 }} /> Dead-Letter</ToggleButton>
                    <ToggleButton value="active"><ArrowDownToLine size={14} style={{ marginRight: 6 }} /> Active</ToggleButton>
                    <ToggleButton value="both"><Layers size={14} style={{ marginRight: 6 }} /> Both</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small" variant="outlined"
                    startIcon={sampling ? <CircularProgress size={14} color="inherit" /> : <Sparkles size={14} />}
                    onClick={handleLoadFields} disabled={sampling}
                    sx={{
                      borderColor: '#01426A', color: '#01426A', borderRadius: '8px',
                      textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                      '&:hover': { borderColor: '#012c47', bgcolor: 'rgba(1,66,106,0.04)' },
                    }}
                  >
                    {sampling ? 'Loading…' : 'Load fields from message'}
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={addCriterion}
                    sx={{
                      borderColor: '#0097A9', color: '#0097A9', borderRadius: '8px',
                      textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                      '&:hover': { borderColor: '#007f8f', bgcolor: 'rgba(0,151,169,0.04)' },
                    }}
                  >
                    Add field
                  </Button>
                </Stack>
              </Stack>

              {sampleInfo && (
                <Chip
                  size="small" icon={<Sparkles size={12} />} label={sampleInfo}
                  sx={{ mb: 2, bgcolor: '#eff6ff', color: '#01426A', fontWeight: 600, '& .MuiChip-icon': { color: '#01426A' } }}
                />
              )}

              <Stack spacing={1.5}>
                {criteria.map((c, idx) => (
                  <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
                    <Autocomplete
                      freeSolo
                      options={fieldOptions}
                      value={c.key ? (fieldOptions.find(o => o.path === c.key) || { path: c.key }) : null}
                      isOptionEqualToValue={(o, v) =>
                        (typeof o === 'string' ? o : o.path) === (typeof v === 'string' ? v : v?.path)
                      }
                      onChange={(_, val) =>
                        updateCriterion(idx, 'key', val == null ? '' : (typeof val === 'string' ? val : val.path))
                      }
                      getOptionLabel={(o) => leafOf(typeof o === 'string' ? o : o.path)}
                      filterOptions={(opts, state) => {
                        const q = state.inputValue.trim().toLowerCase();
                        const matches = q
                          ? opts.filter(
                              (o) =>
                                (o.path || '').toLowerCase().includes(q) ||
                                leafOf(o.path).toLowerCase().includes(q) ||
                                String(o.sample ?? '').toLowerCase().includes(q),
                            )
                          : opts;
                        return matches.slice(0, 50);
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'block !important', py: 0.75 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a' }}>
                            {leafOf(option.path)}
                          </Typography>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#64748b', display: 'block' }}>
                            {option.path}
                          </Typography>
                          {option.sample !== undefined && option.sample !== '' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              e.g. {String(option.sample)}
                            </Typography>
                          )}
                        </Box>
                      )}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Field" placeholder="Search or pick a field"
                          onKeyDown={handleKeyDown} size="small" sx={inputSx}
                          helperText={c.key || 'Pick a field or type a JSON path'}
                          FormHelperTextProps={{ sx: { fontFamily: 'monospace', fontSize: '0.66rem', color: '#64748b', mx: 0 } }}
                        />
                      )}
                    />
                    <TextField
                      label="Value" placeholder="e.g. 570"
                      value={c.value}
                      onChange={(e) => updateCriterion(idx, 'value', e.target.value)}
                      onKeyDown={handleKeyDown} size="small" sx={{ ...inputSx, flex: 1 }}
                    />
                    {criteria.length > 1 && (
                      <IconButton
                        size="small" onClick={() => removeCriterion(idx)}
                        sx={{ mt: 0.5, color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>

              <Box mt={2.5}>
                <Button
                  variant="contained" onClick={handleSearch} disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search size={16} />}
                  sx={{
                    bgcolor: '#0097A9', borderRadius: '10px', px: 3, py: 1,
                    textTransform: 'none', fontWeight: 600, minWidth: 180,
                    '&:hover': { bgcolor: '#007f8f' },
                  }}
                >
                  {loading ? 'Scanning...' : `Search ${queueTarget === 'both' ? 'Both' : queueTarget === 'active' ? 'Active' : 'Dead-Letter'}`}
                </Button>
                <Typography variant="caption" color="text.secondary" ml={2}>
                  Ctrl+Enter to search
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
                  {error}
                </Alert>
              )}
            </Paper>
          </Fade>

          {/* Loading State */}
          {loading && (
            <Fade in>
              <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '14px', border: '1px solid #e5e7eb' }}>
                <CircularProgress size={40} sx={{ color: '#0097A9', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Scanning {queueTarget === 'both' ? 'active + dead-letter' : queueTarget === 'active' ? 'active queue' : 'dead-letter queue'}… This may take a moment for large queues.
                </Typography>
              </Paper>
            </Fade>
          )}

          {/* Results */}
          {results && !loading && (
            <Fade in timeout={500}>
              <Box>
                {/* Summary */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      icon={<Hash size={14} />}
                      label={`${results.total_scanned.toLocaleString()} messages scanned`}
                      size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }}
                    />
                    <Chip
                      icon={results.match_count > 0 ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                      label={results.match_count > 0
                        ? `${results.match_count} match${results.match_count > 1 ? 'es' : ''} found`
                        : 'No matches found'}
                      size="small"
                      sx={{
                        bgcolor: results.match_count > 0 ? '#f0fdf4' : '#fef2f2',
                        color: results.match_count > 0 ? '#16a34a' : '#dc2626',
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {criteria.filter(c => c.key && c.value).map((c, i) => (
                      <Chip
                        key={i} size="small" variant="outlined"
                        label={`${leafOf(c.key)} = "${c.value}"`}
                        sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                      />
                    ))}
                  </Stack>
                </Paper>

                {/* Match Cards */}
                {results.matches.length > 0 && (
                  <Stack spacing={2}>
                    {results.matches.map((match, idx) => (
                      <Grow in key={match.sequence_number} timeout={300 + idx * 80}>
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: '14px', border: '1px solid #e5e7eb',
                            overflow: 'hidden', transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)' },
                          }}
                        >
                          {/* Card Header */}
                          <Box
                            sx={{
                              px: 2.5, py: 1.5,
                              background: 'linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%)',
                              borderBottom: '1px solid #e5e7eb',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleCard(match.sequence_number)}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box sx={{
                                width: 36, height: 36, borderRadius: '10px', bgcolor: '#1e293b',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <Inbox size={18} color="#fff" />
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={700} color="#01426A">
                                  Sequence #{match.sequence_number}
                                  <CopyBtn text={String(match.sequence_number)} />
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {match.subject || match.content_type || 'Message'}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {match.source && (
                                <Chip
                                  label={match.source === 'deadletter' ? 'Dead-Letter' : 'Active'}
                                  size="small"
                                  sx={{
                                    bgcolor: match.source === 'deadletter' ? '#fef2f2' : '#f0fdf4',
                                    color: match.source === 'deadletter' ? '#dc2626' : '#16a34a',
                                    fontWeight: 600, fontSize: '0.65rem',
                                    border: `1px solid ${match.source === 'deadletter' ? '#dc262622' : '#16a34a22'}`,
                                  }}
                                />
                              )}
                              <DLReasonChip reason={match.dead_letter_reason} />
                              {expandedCards[match.sequence_number]
                                ? <ChevronUp size={16} color="#64748b" />
                                : <ChevronDown size={16} color="#64748b" />}
                            </Stack>
                          </Box>

                          {/* Card Body */}
                          <Box sx={{ px: 2.5, py: 2 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={{ xs: 1.5, sm: 4 }}
                              divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                            >
                              <DetailItem icon={<Clock size={14} />} label="Enqueued" value={formatDateTime(match.enqueued_at)} />
                              <DetailItem icon={<Hash size={14} />} label="Seq #" value={match.sequence_number} />
                              <DetailItem icon={<Database size={14} />} label="Type" value={match.body_type === 'json' ? 'JSON' : 'Raw'} />
                            </Stack>

                            {match.dead_letter_description && (
                              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '0.8rem' }}>
                                {match.dead_letter_description}
                              </Alert>
                            )}
                          </Box>

                          {/* Expandable Body Preview */}
                          <Collapse in={expandedCards[match.sequence_number]}>
                            <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                  Message Body{match.body_size ? ` · ${formatBytes(match.body_size)}` : ''}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Tooltip title="View full message">
                                    <IconButton size="small" onClick={() => setViewer(match)}
                                      sx={{ '&:hover': { bgcolor: 'rgba(0,151,169,0.08)' } }}>
                                      <Maximize2 size={14} color="#64748b" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton size="small"
                                      onClick={() => downloadText(fileNameFor(match), match.body_full ?? match.body_preview)}
                                      sx={{ '&:hover': { bgcolor: 'rgba(0,151,169,0.08)' } }}>
                                      <Download size={14} color="#64748b" />
                                    </IconButton>
                                  </Tooltip>
                                  <CopyBtn text={match.body_full ?? match.body_preview} />
                                </Stack>
                              </Stack>
                              <Box
                                component="pre"
                                sx={{
                                  p: 1.5, borderRadius: '8px', bgcolor: '#1e293b', color: '#e2e8f0',
                                  fontSize: '0.75rem', fontFamily: 'monospace', overflow: 'auto',
                                  maxHeight: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0,
                                }}
                              >
                                {match.body_full ?? match.body_preview}
                              </Box>
                            </Box>
                          </Collapse>
                        </Paper>
                      </Grow>
                    ))}
                  </Stack>
                )}

                {/* No matches */}
                {results.match_count === 0 && (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '14px', border: '1px solid #e5e7eb' }}>
                    <Inbox size={48} color="#94a3b8" />
                    <Typography variant="h6" color="text.secondary" mt={1.5}>
                      No Matches Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No messages matched your criteria across {results.total_scanned.toLocaleString()} messages scanned.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Fade>
          )}
        </>
      )}

      <ConnectionDialog
        open={dialogOpen}
        initial={dialogInitial}
        onClose={() => setDialogOpen(false)}
        onSaved={handleDialogSaved}
      />

      {/* Full message viewer */}
      <Dialog
        open={Boolean(viewer)} onClose={() => setViewer(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Inbox size={18} color="#0097A9" />
              <Typography variant="subtitle1" fontWeight={700}>
                Message #{viewer?.sequence_number}
              </Typography>
              {viewer?.body_size ? (
                <Chip size="small" label={formatBytes(viewer.body_size)} sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
              ) : null}
            </Stack>
            <IconButton size="small" onClick={() => setViewer(null)}><X size={18} /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#eef2f7' }}>
          <Box
            component="pre"
            sx={{
              p: 2, borderRadius: '10px', bgcolor: '#1e293b', color: '#e2e8f0',
              fontSize: '0.78rem', fontFamily: 'monospace', overflow: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, maxHeight: '65vh',
            }}
          >
            {viewer?.body_full ?? viewer?.body_preview}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => navigator.clipboard.writeText(viewer?.body_full ?? viewer?.body_preview ?? '')}
            startIcon={<Copy size={15} />}
            sx={{ textTransform: 'none', color: '#475569' }}
          >
            Copy
          </Button>
          <Button
            variant="contained"
            onClick={() => viewer && downloadText(fileNameFor(viewer), viewer.body_full ?? viewer.body_preview)}
            startIcon={<Download size={15} />}
            sx={{ bgcolor: '#0097A9', textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3, '&:hover': { bgcolor: '#007f8f' } }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ── Helper Components ── */
function DetailItem({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ color: '#64748b', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} color="#1f2937">
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

/* ── Formatters ── */
function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZoneName: 'short',
  });
}

function formatBytes(n) {
  if (n === undefined || n === null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileNameFor(match) {
  const ext = match?.body_type === 'json' ? 'json' : 'txt';
  return `servicebus-message-${match?.sequence_number ?? 'message'}.${ext}`;
}

/* Trigger a client-side download of text content. */
function downloadText(filename, content) {
  const blob = new Blob([content ?? ''], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default ServiceBusExplorer;
